import { useMemo } from "react";
import type { FieldValues, Path } from "react-hook-form";

import CurrencyInput from "~/components/formElements/CurrencyInput";
import FormInputError from "~/components/formElements/FormInputError";
import Headline from "~/components/Headline";
import InvoiceTotalsDetail from "~/modules/billing/components/InvoiceTotalsDetail";
import InvoiceTotalsDetailLabel
	from "~/modules/billing/components/InvoiceTotalsDetail/components/InvoiceTotalsDetailLabel";
import InvoiceTotalsDetailValue
	from "~/modules/billing/components/InvoiceTotalsDetail/components/InvoiceTotalsDetailValue";
import { formatNumberToCurrency } from "~/utils/currencyUtils.ts";

import type { BaseInvoiceTotalsFormSectionType, InvoiceTotalsFormSectionProps } from "./invoiceTotalsFormSectionTypes";

const InvoiceTotalsFormSection = <T extends FieldValues & BaseInvoiceTotalsFormSectionType>({
	control,
	errorMessage,
	watch,
	title,
	includeTravelCosts = true,
}: InvoiceTotalsFormSectionProps<T>) => {
	const subtotalCents = watch("subtotalCents" as Path<T>);
	const vatPercentage = watch("vatPercentage" as Path<T>);
	const travelCostCentsBeforeVat = includeTravelCosts
		? watch("travelCostCentsBeforeVat" as Path<T>)
		: 0;

	const [invoiceSubTotalWithTravelCostsCents, vatCents, grandTotalCents] = useMemo(() => {
		const invoiceSubTotalWithTravelCostsCents = subtotalCents + (travelCostCentsBeforeVat || 0);
		const vatCents = Math.round(invoiceSubTotalWithTravelCostsCents * (vatPercentage || 0) / 10000);
		const grandTotalCents = invoiceSubTotalWithTravelCostsCents + vatCents;
		return [invoiceSubTotalWithTravelCostsCents, vatCents, grandTotalCents];
	}, [subtotalCents, travelCostCentsBeforeVat, vatPercentage]);

	return (
		<div className="col-span-2 grid grid-cols-2 gap-x-4">
			{title && <div className="col-span-2">
				<Headline className="mb-2"
						  type="h4">{title}</Headline>
			</div>}
			<InvoiceTotalsDetail label="Summe Einzelpositionen">
				<CurrencyInput
					name="subtotalCents"
					control={control}
					textAlign="right"
					inputSize="sm"
				/>
			</InvoiceTotalsDetail>

			{includeTravelCosts && (
				<InvoiceTotalsDetail label="Reisekosten">
					<CurrencyInput
						name="travelCostCentsBeforeVat"
						control={control}
						textAlign="right"
						inputSize="sm"
					/>
				</InvoiceTotalsDetail>
			)}

			<div className="col-span-2 mt-1 h-px w-full bg-gray-200" />

			<InvoiceTotalsDetail label="Rechnungssumme">
				<div className="pr-1.5 text-right text-sm">
					{formatNumberToCurrency(invoiceSubTotalWithTravelCostsCents)}
				</div>
			</InvoiceTotalsDetail>

			<InvoiceTotalsDetailLabel>
				<span>MwSt</span>
				<div className="mx-2 w-16">
					<CurrencyInput
						name="vatPercentage"
						textAlign="right"
						min={0}
						maxValue={10000}
						control={control}
						inputSize="sm"
					/>
				</div>
				<span>%</span>
			</InvoiceTotalsDetailLabel>

			<InvoiceTotalsDetailValue>
				<div className="pr-1.5 text-right text-sm">
					{formatNumberToCurrency(vatCents)}
				</div>
			</InvoiceTotalsDetailValue>

			<div className="col-span-2 mt-1 h-px w-full bg-gray-200" />

			<InvoiceTotalsDetailLabel bold={true}>
				Gesamt
			</InvoiceTotalsDetailLabel>

			<InvoiceTotalsDetailValue bold={true}>
				<div className="pr-1.5 text-right text-sm">
					{formatNumberToCurrency(grandTotalCents)}
				</div>
			</InvoiceTotalsDetailValue>
			{errorMessage && <div className="col-span-2"><FormInputError>{errorMessage}</FormInputError></div> }
		</div>
	);
};

export default InvoiceTotalsFormSection;